import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  CircularProgress, 
  Alert,
  Fade,
  Zoom,
  Slide
} from '@mui/material';
import { 
  CheckCircle, 
  Error as ErrorIcon, 
  Security as SecurityIcon 
} from '@mui/icons-material';

const AuthRedirect = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setToken } = useAuth();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('Verifying authentication...');

  useEffect(() => {
    const accessToken = searchParams.get('accesstoken');
    const idToken = searchParams.get('idtoken');
    
    if (!accessToken) {
      setStatus('error');
      setMessage('No access token provided');
      return;
    }

    // Store the tokens and redirect to dashboard
    try {
      // Verify access token format (basic check)
      const accessTokenParts = accessToken.split('.');
      if (accessTokenParts.length !== 3) {
        throw new Error('Invalid access token format');
      }
      
      // Verify ID token format if provided (basic check)
      if (idToken) {
        const idTokenParts = idToken.split('.');
        if (idTokenParts.length !== 3) {
          throw new Error('Invalid ID token format');
        }
      }
      
      // Use the setToken method from AuthContext with both tokens
      setToken(accessToken, idToken || undefined);
      
      setStatus('success');
      setMessage('Authentication successful! Redirecting...');
      
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/');
      }, 1500);
      
    } catch (error) {
      console.error('Token validation error:', error);
      setStatus('error');
      setMessage('Invalid authentication tokens');
      
      // Redirect to login after error
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    }
  }, [searchParams, navigate, setToken]);

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return (
          <Zoom in={status === 'loading'} timeout={800}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                mx: 'auto'
              }}
            >
              <CircularProgress size={40} sx={{ color: 'white' }} />
            </Box>
          </Zoom>
        );
      case 'success':
        return (
          <Zoom in={status === 'success'} timeout={800}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 32px rgba(76, 175, 80, 0.3)',
                mx: 'auto'
              }}
            >
              <CheckCircle sx={{ fontSize: 40, color: 'white' }} />
            </Box>
          </Zoom>
        );
      case 'error':
        return (
          <Zoom in={status === 'error'} timeout={800}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 32px rgba(244, 67, 54, 0.3)',
                mx: 'auto'
              }}
            >
              <ErrorIcon sx={{ fontSize: 40, color: 'white' }} />
            </Box>
          </Zoom>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
          animation: 'float 20s linear infinite',
          zIndex: 0
        }}
      />

      <Fade in timeout={1000}>
        <Card
          sx={{
            maxWidth: 450,
            width: '100%',
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
            backdropFilter: 'blur(10px)',
            background: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            zIndex: 1
          }}
        >
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <Slide direction="up" in timeout={800}>
              <Box sx={{ mb: 4 }}>
                {getStatusIcon()}
              </Box>
            </Slide>
            
            <Fade in timeout={1200}>
              <Box>
                <Typography 
                  variant="h4" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 2
                  }}
                >
                  {status === 'loading' && 'Authenticating...'}
                  {status === 'success' && 'Authentication Successful'}
                  {status === 'error' && 'Authentication Failed'}
                </Typography>
                
                <Typography 
                  variant="body1" 
                  color="text.secondary" 
                  sx={{ mb: 3, fontSize: '1.1rem' }}
                >
                  {message}
                </Typography>
                
                {status === 'success' && (
                  <Fade in={status === 'success'} timeout={1500}>
                    <Alert 
                      severity="success" 
                      sx={{ 
                        mt: 2,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
                        border: 'none'
                      }}
                    >
                      You will be redirected to the dashboard shortly.
                    </Alert>
                  </Fade>
                )}
                
                {status === 'error' && (
                  <Fade in={status === 'error'} timeout={1500}>
                    <Box>
                      <Alert 
                        severity="error" 
                        sx={{ 
                          mt: 2,
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
                          border: 'none',
                          mb: 3,
                          '& .MuiAlert-icon': {
                            color: '#f44336'
                          }
                        }}
                      >
                        Please try logging in again through the main application.
                      </Alert>
                    </Box>
                  </Fade>
                )}
              </Box>
            </Fade>
          </CardContent>
        </Card>
      </Fade>

      <style jsx>{`
        @keyframes float {
          0% { transform: translateY(0px) rotate(0deg); }
          100% { transform: translateY(-20px) rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};

export default AuthRedirect;